import React, {useEffect, useRef} from 'react';
import Hls from 'hls.js';
import {useSearchParams} from 'react-router-dom';
import {ReactFlvPlayer} from 'self-flv-player';

const STREAM_SERVER = window._env_?.STREAM_SERVER || '';

const View = () => {
    const [params] = useSearchParams();
    const url = params.get('url');
    const referer = params.get('referer');
    const videoRef = useRef(null);
    const flvPlayerRef = useRef(null);
    const hlsInstanceRef = useRef(null);

    const isFlv = url?.includes('fw?');
    const streamParams = new URLSearchParams({
        url: url || '',
        referer: referer || '',
    });
    const streamSrc = `${STREAM_SERVER}/stream?${streamParams.toString()}`;

    useEffect(() => {
        if (isFlv || !videoRef.current || !url) return;

        const video = videoRef.current;

        // Cleanup previous instance
        if (hlsInstanceRef.current) {
            hlsInstanceRef.current.destroy();
            hlsInstanceRef.current = null;
        }

        // 1. Safari / iOS / Apple Native HLS check (native decode supports 4K HEVC 10-bit directly)
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = streamSrc;
            return;
        }

        // 2. Modern Hls.js (v1.5+) check
        if (Hls.isSupported()) {
            const hls = new Hls({
                maxBufferLength: 300,
                maxMaxBufferLength: 600,
                backBufferLength: 300,
                liveSyncDurationCount: 10,
                enableWorker: true,
                lowLatencyMode: false,
                maxBufferSize: 200 * 1000 * 1000,
            });

            hls.loadSource(streamSrc);
            hls.attachMedia(video);
            hlsInstanceRef.current = hls;

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.warn('[Hls.js] Network error, recovering...');
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.warn('[Hls.js] Media error, recovering...');
                            hls.recoverMediaError();
                            break;
                        default:
                            console.error('[Hls.js] Fatal unrecoverable error:', data);
                            hls.destroy();
                            break;
                    }
                }
            });
        } else {
            video.src = streamSrc;
        }

        return () => {
            if (hlsInstanceRef.current) {
                hlsInstanceRef.current.destroy();
                hlsInstanceRef.current = null;
            }
        };
    }, [url, referer, isFlv, streamSrc]);

    const handleTogglePictureInPicture = () => {
        if (document.pictureInPictureElement) {
            document.exitPictureInPicture();
        } else {
            if (isFlv && flvPlayerRef.current?.myRef?.current) {
                flvPlayerRef.current.myRef.current.requestPictureInPicture();
            } else if (videoRef.current) {
                videoRef.current.requestPictureInPicture();
            }
        }
    };

    return (
        <div style={{backgroundColor: 'black', height: '100vh', position: 'relative'}}>
            {isFlv ? (
                <ReactFlvPlayer ref={flvPlayerRef} url={`${url}&referer=${referer}`} />
            ) : (
                <video
                    ref={videoRef}
                    controls
                    playsInline
                    style={{width: '100%', height: '100%', objectFit: 'contain'}}
                />
            )}
            <button
                onClick={handleTogglePictureInPicture}
                style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    zIndex: 10,
                    padding: '8px 16px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.3)',
                    cursor: 'pointer'
                }}
            >
                PIP
            </button>
        </div>
    );
};

export default View;
